import React from "react";
import { CheckCircle2, MinusCircle, FileQuestion } from "lucide-react";

/**
 * Liste des questions/réponses du questionnaire conditionnel — groupé par section.
 * Affiche uniquement les champs réellement visibles (résolus côté backend).
 *
 * Props :
 *   visibleFields : array de fields visibles
 *   responses     : { field_id: value }
 *   questionnaire : { name, description }
 */
const formatValue = (value, field) => {
  if (value === null || value === undefined || value === "") return null;
  if (Array.isArray(value)) return value.length ? value.join(", ") : null;
  if (typeof value === "boolean") return value ? "Oui" : "Non";
  if (field?.type === "date") {
    try { return new Date(value).toLocaleDateString("fr-FR"); } catch { return String(value); }
  }
  return String(value);
};

const TYPE_BADGE = {
  text: { label: "Texte", color: "#3b82f6" },
  textarea: { label: "Texte long", color: "#3b82f6" },
  select: { label: "Choix", color: "#8b5cf6" },
  checkbox: { label: "Cases", color: "#8b5cf6" },
  date: { label: "Date", color: "#06b6d4" },
  number: { label: "Nombre", color: "#f59e0b" },
  email: { label: "Email", color: "#22c55e" },
  phone: { label: "Téléphone", color: "#22c55e" },
};

export default function QuestionnaireResponsesList({
  visibleFields = [],
  responses = {},
  questionnaire,
  testId = "qst-responses-list",
}) {
  if (!visibleFields.length) {
    return (
      <div className="text-center py-8 text-sm text-slate-500" data-testid={`${testId}-empty`}>
        <FileQuestion className="w-8 h-8 mx-auto mb-2 text-slate-600" />
        Aucune réponse au questionnaire pour ce prospect.
      </div>
    );
  }

  // Group by section
  const sections = {};
  for (const f of visibleFields) {
    const sec = f.section || "Général";
    if (!sections[sec]) sections[sec] = [];
    sections[sec].push(f);
  }

  return (
    <div className="space-y-4" data-testid={testId}>
      {questionnaire && (
        <div className="bg-violet-500/10 border border-violet-500/20 rounded-xl p-3">
          <div className="text-xs font-bold text-violet-300">{questionnaire.name}</div>
          {questionnaire.description && (
            <div className="text-[11px] text-slate-400 mt-0.5">{questionnaire.description}</div>
          )}
        </div>
      )}

      {Object.entries(sections).map(([sectionName, fields]) => (
        <div key={sectionName} className="bg-[#0b0811] rounded-xl border border-white/5 p-4" data-testid={`qst-section-${sectionName.replace(/\s+/g, "-").toLowerCase()}`}>
          <h4 className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-3 flex items-center gap-1.5">
            <span className="w-6 h-px bg-white/10" />
            {sectionName}
            <span className="flex-1 h-px bg-white/10" />
          </h4>
          <ul className="space-y-3">
            {fields.map((f) => {
              const value = formatValue(responses[f.field_id], f);
              const isAnswered = value !== null;
              const typeMeta = TYPE_BADGE[f.type] || { label: f.type, color: "#64748b" };
              return (
                <li
                  key={f.field_id}
                  className="border-l-2 pl-3 py-1"
                  style={{ borderLeftColor: isAnswered ? "#22c55e44" : "#64748b44" }}
                  data-testid={`qst-row-${f.field_id}`}
                >
                  <div className="flex items-start gap-2">
                    {isAnswered ? (
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0 mt-1" />
                    ) : (
                      <MinusCircle className="w-3.5 h-3.5 text-slate-600 flex-shrink-0 mt-1" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-xs font-semibold text-slate-200">
                          {f.label}
                          {f.required && <span className="text-rose-400 ml-0.5">*</span>}
                        </span>
                        <span
                          className="text-[9px] px-1.5 py-0.5 rounded-full font-medium"
                          style={{ background: typeMeta.color + "20", color: typeMeta.color }}
                        >
                          {typeMeta.label}
                        </span>
                      </div>
                      {isAnswered ? (
                        <div className="text-sm text-white mt-0.5 break-words whitespace-pre-wrap leading-snug" data-testid={`qst-value-${f.field_id}`}>
                          {value}
                        </div>
                      ) : (
                        <div className="text-xs text-slate-500 italic mt-0.5">Non renseigné</div>
                      )}
                      {f.placeholder && !isAnswered && (
                        <div className="text-[10px] text-slate-600 italic mt-0.5">Aide : {f.placeholder}</div>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </div>
  );
}
